import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import LinkIcon from "@mui/icons-material/Link";
import SettingsIcon from "@mui/icons-material/Settings";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useHistory, useParams } from "react-router-dom";
import * as QRCode from "qrcode";
import PageHeader from "../../app/layout/PageHeader";
import { api } from "../../api/apiClient";
import PartySearch from "../components/PartySearch";
import ServiceMemberFilters from "../../components/service-members/ServiceMemberFilters";
import ServiceMemberList from "../../components/service-members/ServiceMemberList";
import { notify } from "../components-ui/toast";
import { useStaticData } from "../../context/StaticDataContext";
import { useAuth } from "../../context/AuthContext";
import {
  RegistrationMemberType,
  RegistrationRequirementLevel,
  Party,
  PartySummaryDto,
  ServiceDto,
  ServiceMemberDto,
  ServiceRegistrationMemberTypeStatic,
  ServiceRegistrationRequirementDto,
} from "../types/types";
import "../../css/buttons.css";
import "../../css/cards.css";
import "../../css/forms.css";
import "../../css/tables.css";
import "./ServiceMembersPage.css";

interface RouteParams {
  serviceId: string;
}

interface ProfileInvitationResult {
  token: string;
  profilePath: string;
  expiresDate: string;
}

interface ProfileUpdateLink {
  member: ServiceMemberDto;
  token: string;
  url: string;
  expiresDate: string;
}

interface RegistrationInvitationResult {
  channel: "Email" | "SMS";
  status: string;
  registrationPath: string;
  outboundMessageId?: number | null;
}

interface ExistingRegistrationLink {
  partyId: number;
  token: string;
  url: string;
  expiresDate: string;
}

const memberTypeOptions: Array<{
  value: RegistrationMemberType;
  label: string;
}> = [
  { value: "ServiceUser", label: "Service user" },
  { value: "PrimaryContact", label: "Primary contact" },
  { value: "Volunteer", label: "Volunteer" },
  { value: "Staff", label: "Staff" },
];

const requirementFallbacks: Record<
  string,
  { label: string; description: string }
> = {
  PrimaryContact: {
    label: "Primary contact",
    description: "Captures the main adult or person being registered.",
  },
  ContactDetails: {
    label: "Contact details",
    description: "Captures phone and email details.",
  },
  Address: {
    label: "Address",
    description: "Captures the person or household address.",
  },
  Household: {
    label: "Family / household",
    description: "Captures family or household relationships.",
  },
  YouthProfile: {
    label: "Child profile",
    description: "Captures child-specific youth profile information.",
  },
  EmergencyContact: {
    label: "Emergency contact",
    description: "Captures a non-party contact to use in an emergency.",
  },
  ServiceMembership: {
    label: "Service registration",
    description: "Enrolls the person or family into the service.",
  },
  ServiceRoles: {
    label: "Membership roles",
    description: "Assigns one or more roles on the service membership.",
  },
  Agreements: {
    label: "Agreements",
    description: "Shows applicable service agreements during registration.",
  },
  Consents: {
    label: "Consents",
    description: "Shows applicable consent questions during registration.",
  },
  Review: {
    label: "Review",
    description: "Shows a final review step before saving.",
  },
};

const requirementLevelOptions: Array<{
  value: RegistrationRequirementLevel;
  label: string;
  hint: string;
}> = [
  { value: "Hidden", label: "Hidden", hint: "Do not show this section" },
  { value: "Optional", label: "Optional", hint: "Show, but do not require it" },
  { value: "Required", label: "Required", hint: "Show and require completion" },
];

const requirementLevelShortLabels: Record<
  RegistrationRequirementLevel,
  string
> = {
  Hidden: "Hide",
  Optional: "Opt",
  Required: "Req",
};

const registrationMemberTypeLabel = (memberType: RegistrationMemberType) => {
  switch (memberType) {
    case "PrimaryContact":
      return "Primary contact";
    case "Volunteer":
      return "Volunteer";
    case "Staff":
      return "Staff";
    default:
      return "Service user";
  }
};

const requirementGroups = [
  {
    title: "Person details",
    description: "Core information about the person or primary family contact.",
    componentKeys: ["PrimaryContact", "ContactDetails", "Address"],
  },
  {
    title: "Family and child details",
    description: "Household relationships and child-specific information.",
    componentKeys: ["Household", "YouthProfile", "EmergencyContact"],
  },
  {
    title: "Service registration",
    description: "How the person is enrolled and classified in this service.",
    componentKeys: ["ServiceMembership", "ServiceRoles"],
  },
  {
    title: "Completion",
    description: "Agreements, consent questions and final review.",
    componentKeys: ["Agreements", "Consents", "Review"],
  },
];

export default function ServiceMembersPage() {
  const { canSendCommunications } = useAuth();
  const history = useHistory();
  const { data } = useStaticData();
  const { serviceId } = useParams<RouteParams>();
  const numericServiceId = Number(serviceId);
  const registrationComponentText = useMemo(() => {
    const map = new Map(
      (data?.serviceRegistrationComponents ?? []).map((component) => [
        component.code.toLowerCase(),
        {
          label: component.name,
          description:
            component.description ??
            requirementFallbacks[component.code]?.description ??
            "",
        },
      ]),
    );

    return (componentKey: string) =>
      map.get(componentKey.toLowerCase()) ??
      requirementFallbacks[componentKey] ?? {
        label: componentKey,
        description: "",
      };
  }, [data?.serviceRegistrationComponents]);

  const [members, setMembers] = useState<ServiceMemberDto[]>([]);
  const [service, setService] = useState<ServiceDto | null>(null);
  const [configuredMemberTypes, setConfiguredMemberTypes] = useState<
    ServiceRegistrationMemberTypeStatic[]
  >([]);
  const [search, setSearch] = useState("");
  const [expandedPartyId, setExpandedPartyId] = useState<number | null>(null);
  const [creatingProfileUpdatePartyId, setCreatingProfileUpdatePartyId] =
    useState<number | null>(null);
  const [profileUpdateLink, setProfileUpdateLink] =
    useState<ProfileUpdateLink | null>(null);
  const [sendingProfileUpdateTo, setSendingProfileUpdateTo] = useState<
    string | null
  >(null);
  const [registrationInviteOpen, setRegistrationInviteOpen] = useState(false);
  const [registrationInviteMode, setRegistrationInviteMode] = useState<
    "new" | "existing"
  >("new");
  const [registrationInviteParty, setRegistrationInviteParty] =
    useState<Party | null>(null);
  const registrationInvitePartyIdRef = useRef<number | null>(null);
  const [registrationInvitePartySummary, setRegistrationInvitePartySummary] =
    useState<PartySummaryDto | null>(null);
  const [registrationInvitePartyLoading, setRegistrationInvitePartyLoading] =
    useState(false);
  const [existingRegistrationLink, setExistingRegistrationLink] =
    useState<ExistingRegistrationLink | null>(null);
  const [registrationInviteMemberType, setRegistrationInviteMemberType] =
    useState<RegistrationMemberType>("ServiceUser");
  const [registrationInviteChannel, setRegistrationInviteChannel] = useState<
    "Email" | "SMS"
  >("Email");
  const [registrationInviteRecipient, setRegistrationInviteRecipient] =
    useState("");
  const [registrationInviteInstructions, setRegistrationInviteInstructions] =
    useState("");
  const [registrationQrCode, setRegistrationQrCode] = useState("");
  const [registrationQrCodeError, setRegistrationQrCodeError] =
    useState(false);
  const [sendingRegistrationInvite, setSendingRegistrationInvite] =
    useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requirementsOpen, setRequirementsOpen] = useState(false);
  const [requirementsMemberType, setRequirementsMemberType] =
    useState<RegistrationMemberType>("ServiceUser");
  const [registrationRequirements, setRegistrationRequirements] = useState<
    ServiceRegistrationRequirementDto[]
  >([]);
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [savingRequirements, setSavingRequirements] = useState(false);
  const configuredMemberTypeOptions = useMemo(
    () =>
      configuredMemberTypes
        .filter(
          (memberType) =>
            !memberType.registrationMode ||
            memberType.registrationMode === "Standalone",
        )
        .map((memberType) => ({
          value: memberType.code,
          label:
            memberType.name ||
            memberTypeOptions.find((option) => option.value === memberType.code)
              ?.label ||
            memberType.code,
          partyType:
            memberType.partyType ??
            (memberType.code === "ServiceUser" ? "Family" : "Person"),
        }))
        .filter((option) =>
          memberTypeOptions.some((known) => known.value === option.value),
        ),
    [configuredMemberTypes],
  );
  const defaultRegistrationMemberType =
    configuredMemberTypeOptions.find((option) => option.value === "ServiceUser")
      ?.value ?? configuredMemberTypeOptions[0]?.value;
  const getRegistrationPartyType = (memberType: RegistrationMemberType) =>
    configuredMemberTypeOptions.find((option) => option.value === memberType)
      ?.partyType ?? (memberType === "ServiceUser" ? "Family" : "Person");
  const registrationInvitePartyType = getRegistrationPartyType(
    registrationInviteMemberType,
  );
  const registrationRequirementGroups = useMemo(
    () =>
      requirementGroups
        .map((group) => ({
          ...group,
          requirements: group.componentKeys
            .map((componentKey) =>
              registrationRequirements.find(
                (requirement) => requirement.componentKey === componentKey,
              ),
            )
            .filter(Boolean) as ServiceRegistrationRequirementDto[],
        }))
        .filter((group) => group.requirements.length > 0),
    [registrationRequirements],
  );

  const loadMembers = useCallback(async () => {
    if (!numericServiceId) return;

    try {
      setError(null);
      const result = await api<ServiceMemberDto[]>(
        `/api/services/${numericServiceId}/members`,
      );
      setMembers(result);
    } catch {
      setError("Failed to load service members.");
    }
  }, [numericServiceId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    if (!numericServiceId) return;

    api<ServiceDto>(`/api/services/${numericServiceId}`, {
      suppressLoading: true,
      notifyError: false,
    })
      .then(setService)
      .catch(() => setService(null));
  }, [numericServiceId]);

  const loadConfiguredMemberTypes = useCallback(async () => {
    if (!numericServiceId) return;

    try {
      const result = await api<ServiceRegistrationMemberTypeStatic[]>(
        `/api/services/${numericServiceId}/registration-requirements/member-types`,
        {
          suppressLoading: true,
          notifyError: false,
        },
      );
      setConfiguredMemberTypes(result);
    } catch {
      setConfiguredMemberTypes([]);
    }
  }, [numericServiceId]);

  useEffect(() => {
    loadConfiguredMemberTypes();
  }, [loadConfiguredMemberTypes]);

  useEffect(() => {
    if (!defaultRegistrationMemberType) return;
    if (
      configuredMemberTypeOptions.some(
        (option) => option.value === registrationInviteMemberType,
      )
    ) {
      return;
    }

    setRegistrationInviteMemberType(defaultRegistrationMemberType);
  }, [
    configuredMemberTypeOptions,
    defaultRegistrationMemberType,
    registrationInviteMemberType,
  ]);

  const loadRegistrationRequirements = useCallback(
    async (memberType: RegistrationMemberType) => {
      if (!numericServiceId) return;

      try {
        setLoadingRequirements(true);
        const result = await api<ServiceRegistrationRequirementDto[]>(
          `/api/services/${numericServiceId}/registration-requirements?memberType=${memberType}`,
          {
            suppressLoading: true,
            notifyError: false,
          },
        );
        setRegistrationRequirements(result);
      } catch (loadError) {
        console.error("Failed to load registration requirements", loadError);
        notify.error("Failed to load registration requirements.");
      } finally {
        setLoadingRequirements(false);
      }
    },
    [numericServiceId],
  );

  useEffect(() => {
    if (!requirementsOpen) return;
    loadRegistrationRequirements(requirementsMemberType);
  }, [loadRegistrationRequirements, requirementsMemberType, requirementsOpen]);

  const updateRequirementLevel = (
    componentKey: string,
    requirementLevel: RegistrationRequirementLevel,
  ) => {
    setRegistrationRequirements((current) =>
      current.map((requirement) =>
        requirement.componentKey === componentKey
          ? { ...requirement, requirementLevel }
          : requirement,
      ),
    );
  };

  const saveRegistrationRequirements = async () => {
    if (!numericServiceId) return;

    try {
      setSavingRequirements(true);
      const result = await api<ServiceRegistrationRequirementDto[]>(
        `/api/services/${numericServiceId}/registration-requirements`,
        {
          method: "PUT",
          body: {
            memberType: requirementsMemberType,
            requirements: registrationRequirements,
          },
        },
      );
      setRegistrationRequirements(result);
      await loadConfiguredMemberTypes();
      notify.success("Registration requirements saved.");
    } catch (saveError) {
      console.error("Failed to save registration requirements", saveError);
      notify.error("Failed to save registration requirements.");
    } finally {
      setSavingRequirements(false);
    }
  };

  const filteredMembers = useMemo(() => {
    const term = search.trim().toLowerCase();

    return members.filter((member) => {
      const matchesSearch =
        !term ||
        `${member.displayName} ${member.partyType} ${member.memberType} ${(member.memberTypes ?? []).join(" ")} ${member.serviceName} ${member.serviceGroupName ?? ""}`
          .toLowerCase()
          .includes(term);

      return matchesSearch;
    });
  }, [members, search]);

  const serviceName = service?.name ?? members[0]?.serviceName ?? "Service";

  const openProfile = (member: ServiceMemberDto) => {
    history.push("/manage-party", {
      partyId: member.partyId,
      partyType: "Family",
      familyInitialStep: 2,
    });
  };

  const createProfileUpdateLink = async (member: ServiceMemberDto) => {
    try {
      setCreatingProfileUpdatePartyId(member.partyId);
      const invitation = await api<ProfileInvitationResult>(
        "/api/profile-invitations",
        {
          method: "POST",
          body: {
            partyId: member.partyId,
            serviceId: member.serviceId,
          },
        },
      );

      setProfileUpdateLink({
        member,
        token: invitation.token,
        url: new URL(invitation.profilePath, window.location.origin).toString(),
        expiresDate: invitation.expiresDate,
      });
    } catch (error) {
      console.error("Failed to create profile update link", error);
      notify.error("Failed to create profile update link.");
    } finally {
      setCreatingProfileUpdatePartyId(null);
    }
  };

  const copyProfileUpdateLink = async () => {
    if (!profileUpdateLink) return;

    try {
      await navigator.clipboard.writeText(profileUpdateLink.url);
      notify.success("Profile update link copied.");
    } catch (error) {
      console.error("Failed to copy profile update link", error);
      notify.error("Copy failed. Select the link and copy it manually.");
    }
  };

  const profileUpdateContacts = profileUpdateLink
    ? [
        ...profileUpdateLink.member.contactSummary,
        ...(profileUpdateLink.member.primaryContact?.contactSummary ?? []),
      ].filter(
        (contact, index, contacts) =>
          contacts.findIndex(
            (candidate) =>
              candidate.contactType === contact.contactType &&
              candidate.value === contact.value,
          ) === index,
      )
    : [];

  const sendProfileUpdate = async (
    channel: "Email" | "SMS",
    recipientAddress: string,
  ) => {
    if (!profileUpdateLink) return;

    const sendingKey = `${channel}:${recipientAddress}`;
    try {
      setSendingProfileUpdateTo(sendingKey);
      await api(
        `/api/profile-invitations/${encodeURIComponent(profileUpdateLink.token)}/send`,
        {
          method: "POST",
          body: { channel, recipientAddress },
        },
      );
      notify.success(
        channel === "Email"
          ? "Profile update email sent."
          : "Profile update SMS queued.",
      );
    } catch (error) {
      console.error(`Failed to send profile update by ${channel}`, error);
      notify.error(`Failed to send profile update by ${channel}.`);
    } finally {
      setSendingProfileUpdateTo(null);
    }
  };

  const isEmailContact = (contactType: string) =>
    contactType.toLowerCase().includes("email");
  const isSmsContact = (contactType: string) => {
    const normalized = contactType.toLowerCase();
    return (
      normalized.includes("mobile") ||
      normalized.includes("cell") ||
      normalized.includes("sms")
    );
  };

  useEffect(() => {
    if (!registrationInviteOpen || registrationInviteMode !== "existing" || !registrationInviteParty) {
      setRegistrationInvitePartySummary(null);
      return;
    }

    let cancelled = false;
    setRegistrationInvitePartyLoading(true);
    api<PartySummaryDto>(`/api/PartyQuery/${registrationInviteParty.partyId}/summary`, {
      suppressLoading: true,
      notifyError: false,
    })
      .then((summary) => {
        if (!cancelled) setRegistrationInvitePartySummary(summary);
      })
      .catch(() => {
        if (!cancelled) {
          setRegistrationInvitePartySummary(null);
          notify.error("Could not load the selected party's contact details.");
        }
      })
      .finally(() => {
        if (!cancelled) setRegistrationInvitePartyLoading(false);
      });

    return () => { cancelled = true; };
  }, [registrationInviteOpen, registrationInviteMode, registrationInviteParty]);

  const registrationInviteContacts = useMemo(() => {
    if (!registrationInvitePartySummary) return [];
    const member = members.find((item) => item.partyId === registrationInvitePartySummary.partyId);
    const contacts = [
      ...registrationInvitePartySummary.contactMethods,
      ...(member?.primaryContact?.contactSummary ?? []),
    ];
    return contacts.filter((contact, index) =>
      contacts.findIndex((item) =>
        item.contactType === contact.contactType && item.value === contact.value) === index);
  }, [members, registrationInvitePartySummary]);

  const registrationInviteChannelContacts = registrationInviteContacts.filter(
    (contact) => registrationInviteChannel === "Email"
      ? isEmailContact(contact.contactType)
      : isSmsContact(contact.contactType),
  );
  const registrationInviteEmailContacts = registrationInviteContacts.filter(
    (contact) => isEmailContact(contact.contactType),
  );
  const registrationInviteSmsContacts = registrationInviteContacts.filter(
    (contact) => isSmsContact(contact.contactType),
  );
  const effectiveRegistrationInviteRecipient =
    registrationInviteMode === "existing"
      ? registrationInviteChannelContacts.find(
          (contact) => contact.value === registrationInviteRecipient,
        )?.value ?? registrationInviteChannelContacts[0]?.value ?? ""
      : registrationInviteRecipient;

  const registrationInviteUrl = useMemo(() => {
    if (!numericServiceId) return "";

    const url = new URL("/register", window.location.origin);
    url.searchParams.set("serviceId", String(numericServiceId));
    url.searchParams.set(
      "partyType",
      registrationInvitePartyType,
    );
    url.searchParams.set("memberType", registrationInviteMemberType);
    return url.toString();
  }, [
    numericServiceId,
    registrationInviteMemberType,
    registrationInvitePartyType,
  ]);

  useEffect(() => {
    if (!registrationInviteOpen || registrationInviteMode !== "existing" || !registrationInvitePartySummary ||
      registrationInvitePartySummary.partyId !== registrationInviteParty?.partyId || !numericServiceId) return;

    let cancelled = false;
    const partyId = registrationInviteParty.partyId;
    api<ProfileInvitationResult>("/api/profile-invitations", {
      method: "POST",
      body: {
        partyId,
        serviceId: numericServiceId,
        includeHousehold: registrationInvitePartyType === "Family",
      },
    })
      .then((invitation) => {
        if (!cancelled && registrationInvitePartyIdRef.current === partyId) {
          setExistingRegistrationLink({
            partyId,
            token: invitation.token,
            url: new URL(invitation.profilePath, window.location.origin).toString(),
            expiresDate: invitation.expiresDate,
          });
        }
      })
      .catch(() => {
        if (!cancelled) notify.error("Could not create a secure update link for this party.");
      });
    return () => { cancelled = true; };
  }, [registrationInviteOpen, registrationInviteMode, registrationInviteParty, registrationInvitePartySummary, numericServiceId, registrationInvitePartyType]);

  const visibleRegistrationInviteUrl = registrationInviteMode === "existing"
    ? existingRegistrationLink?.url ?? ""
    : registrationInviteUrl;

  useEffect(() => {
    if (!registrationInviteOpen || !visibleRegistrationInviteUrl) {
      setRegistrationQrCode("");
      setRegistrationQrCodeError(false);
      return;
    }

    let cancelled = false;
    setRegistrationQrCode("");
    setRegistrationQrCodeError(false);

    QRCode.toDataURL(visibleRegistrationInviteUrl, {
      width: 640,
      margin: 2,
      errorCorrectionLevel: "M",
      color: {
        dark: "#0f172a",
        light: "#ffffff",
      },
    })
      .then((dataUrl) => {
        if (!cancelled) setRegistrationQrCode(dataUrl);
      })
      .catch((qrError) => {
        console.error("Failed to generate registration QR code", qrError);
        if (!cancelled) setRegistrationQrCodeError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [registrationInviteOpen, visibleRegistrationInviteUrl]);

  const openRegistrationInvite = (memberType: RegistrationMemberType) => {
    setRegistrationInviteMemberType(memberType);
    setRegistrationInviteMode("new");
    setRegistrationInviteParty(null);
    registrationInvitePartyIdRef.current = null;
    setRegistrationInvitePartySummary(null);
    setExistingRegistrationLink(null);
    setRegistrationInviteChannel("Email");
    setRegistrationInviteRecipient("");
    setRegistrationInviteInstructions("");
    setRegistrationInviteOpen(true);
  };

  const selectRegistrationInviteParty = (party: Party | null) => {
    registrationInvitePartyIdRef.current = party?.partyId ?? null;
    setRegistrationInviteParty(party);
    setRegistrationInvitePartySummary(null);
    setExistingRegistrationLink(null);
    setRegistrationInviteRecipient("");
  };

  const copyRegistrationInviteLink = async () => {
    if (!visibleRegistrationInviteUrl) return;

    try {
      await navigator.clipboard.writeText(visibleRegistrationInviteUrl);
      notify.success("Registration link copied.");
    } catch (error) {
      console.error("Failed to copy registration link", error);
      notify.error("Copy failed. Select the link and copy it manually.");
    }
  };

  const saveRegistrationQrCode = () => {
    if (!registrationQrCode) return;

    const safeServiceName = (serviceName || "service")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    const safeRegistrationType = (registrationInviteMode === "existing"
      ? "profile-update"
      : registrationMemberTypeLabel(registrationInviteMemberType))
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    const download = document.createElement("a");
    download.href = registrationQrCode;
    download.download = `${safeServiceName || "service"}-${
      safeRegistrationType || "registration"
    }-${registrationInviteMode === "existing" ? "secure-update" : "registration"}-qr.png`;
    document.body.appendChild(download);
    download.click();
    download.remove();
  };

  const sendRegistrationInvite = async () => {
    if (!numericServiceId || !effectiveRegistrationInviteRecipient) return;

    try {
      setSendingRegistrationInvite(true);
      if (registrationInviteMode === "existing") {
        if (!registrationInviteParty || !registrationInvitePartySummary) return;
        const link = existingRegistrationLink;
        if (!link || registrationInvitePartyIdRef.current !== link.partyId) return;
        await api(`/api/profile-invitations/${encodeURIComponent(link.token)}/send`, {
          method: "POST",
          body: {
            channel: registrationInviteChannel,
            recipientAddress: effectiveRegistrationInviteRecipient,
            instructions: registrationInviteInstructions.trim() || null,
          },
        });
      } else {
        await api<RegistrationInvitationResult>(
          "/api/registration-invitations/send",
          {
            method: "POST",
            body: {
              serviceId: numericServiceId,
              partyType: registrationInvitePartyType,
              memberType: registrationInviteMemberType,
              channel: registrationInviteChannel,
              recipientAddress: effectiveRegistrationInviteRecipient,
              instructions: registrationInviteInstructions.trim() || null,
            },
          },
        );
      }
      notify.success(
        registrationInviteChannel === "Email"
          ? registrationInviteMode === "existing" ? "Profile update email sent." : "Registration email sent."
          : registrationInviteMode === "existing" ? "Profile update SMS queued." : "Registration SMS queued.",
      );
      setRegistrationInviteOpen(false);
    } catch (error) {
      console.error("Failed to send registration link", error);
      notify.error("Failed to send registration link.");
    } finally {
      setSendingRegistrationInvite(false);
    }
  };

  const startRegistration = (memberType: RegistrationMemberType) => {
    const partyType = getRegistrationPartyType(memberType);
    history.push(
      `/manage-party?serviceId=${numericServiceId}&memberType=${memberType}&partyType=${partyType}&source=ServiceMembers`,
    );
  };

  return (
    <div className="page-container service-members-page">
      <PageHeader
        title={`${serviceName} Members`}
        subtitle="View and manage parties enrolled in this service."
        actions={
          <>
            <button
              type="button"
              className="icon-button-solid"
              onClick={() => setRequirementsOpen(true)}
              disabled={!numericServiceId}
              title="Configure registration requirements"
              aria-label="Configure registration requirements"
            >
              <SettingsIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="icon-button-solid"
              onClick={() =>
                history.push(
                  `/admin/manage-services?serviceId=${numericServiceId}`,
                )
              }
              title="Back to service management"
              aria-label="Back to service management"
            >
              <ArrowBackIcon fontSize="small" />
            </button>
          </>
        }
      />

      <section className="card service-members-toolbar">
        <ServiceMemberFilters search={search} onSearchChange={setSearch} />
        <div className="service-member-registration-actions">
          {configuredMemberTypeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`service-member-registration-action${
                option.value === "ServiceUser"
                  ? " service-member-registration-action--primary"
                  : ""
              }`}
              disabled={!numericServiceId}
              onClick={() => startRegistration(option.value)}
            >
              <AddIcon fontSize="small" />
              <span>{registrationMemberTypeLabel(option.value)}</span>
            </button>
          ))}
          <button
            type="button"
            className="service-member-registration-action service-member-registration-action--link"
            disabled={!numericServiceId || !defaultRegistrationMemberType}
            onClick={() =>
              defaultRegistrationMemberType &&
              openRegistrationInvite(defaultRegistrationMemberType)
            }
          >
            <LinkIcon fontSize="small" />
            <span>Send link</span>
          </button>
        </div>
      </section>

      {error && <div className="notification notification--error">{error}</div>}

      <ServiceMemberList
        members={filteredMembers}
        expandedPartyId={expandedPartyId}
        onOpenProfile={openProfile}
        onCreateProfileUpdate={createProfileUpdateLink}
        onMembershipChanged={loadMembers}
        creatingProfileUpdatePartyId={creatingProfileUpdatePartyId}
        onToggleExpanded={(partyId) =>
          setExpandedPartyId((current) =>
            current === partyId ? null : partyId,
          )
        }
      />

      <Dialog
        open={Boolean(profileUpdateLink)}
        onClose={() => setProfileUpdateLink(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Profile update link</DialogTitle>
        <DialogContent>
          {profileUpdateLink && (
            <div style={{ display: "grid", gap: "1rem", paddingTop: "0.5rem" }}>
              <div>
                Send this secure link to{" "}
                <strong>{profileUpdateLink.member.displayName}</strong>. It
                opens their existing details for review and correction.
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="profile-update-link">
                  Profile link
                </label>
                <input
                  id="profile-update-link"
                  className="form-input"
                  value={profileUpdateLink.url}
                  readOnly
                  onFocus={(event) => event.currentTarget.select()}
                />
              </div>

              <div className="field-hint" style={{ marginBottom: 0 }}>
                Expires{" "}
                {new Date(profileUpdateLink.expiresDate).toLocaleString(
                  "en-GB",
                )}
                .
              </div>

              <div>
                <div className="form-label">Available contact details</div>
                {profileUpdateContacts.length > 0 ? (
                  profileUpdateContacts.map((contact) => (
                    <div
                      key={`${contact.contactType}:${contact.value}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.75rem",
                      }}
                    >
                      <span className="field-hint" style={{ marginBottom: 0 }}>
                        {contact.contactType}: {contact.value}
                      </span>
                      {isEmailContact(contact.contactType) && (
                        <Button
                          size="small"
                          startIcon={<EmailOutlinedIcon />}
                          disabled={!canSendCommunications || sendingProfileUpdateTo !== null}
                          onClick={() =>
                            sendProfileUpdate("Email", contact.value)
                          }
                        >
                          Send email
                        </Button>
                      )}
                      {isSmsContact(contact.contactType) && (
                        <Button
                          size="small"
                          startIcon={<SmsOutlinedIcon />}
                          disabled={!canSendCommunications || sendingProfileUpdateTo !== null}
                          onClick={() =>
                            sendProfileUpdate("SMS", contact.value)
                          }
                        >
                          Send SMS
                        </Button>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="field-hint">
                    No email address or mobile number is recorded.
                  </div>
                )}
              </div>

              <div className="field-hint" style={{ marginBottom: 0 }}>
                Confirm the destination before sending. SMS is dispatched by the
                existing messaging worker; email currently uses the direct email
                sender.
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileUpdateLink(null)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={copyProfileUpdateLink}
          >
            Copy link
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={registrationInviteOpen}
        onClose={() => setRegistrationInviteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Send registration link</DialogTitle>
        <DialogContent>
          <div style={{ display: "grid", gap: "1rem", paddingTop: "0.5rem" }}>
            <fieldset className="registration-invite-recipient-mode">
              <legend className="form-label">Who is this for?</legend>
              <label>
                <input
                  type="radio"
                  name="registration-invite-mode"
                  checked={registrationInviteMode === "new"}
                  onChange={() => {
                    setRegistrationInviteMode("new");
                    selectRegistrationInviteParty(null);
                  }}
                />
                New parent / party
              </label>
              <label>
                <input
                  type="radio"
                  name="registration-invite-mode"
                  checked={registrationInviteMode === "existing"}
                  onChange={() => {
                    setRegistrationInviteMode("existing");
                    setRegistrationInviteRecipient("");
                  }}
                />
                Existing parent / party
              </label>
            </fieldset>

            <div className="form-row">
              {registrationInviteMode === "new" && <div className="form-group">
                <label className="form-label">Registration type</label>
                <select
                  className="form-input"
                  value={registrationInviteMemberType}
                  onChange={(event) =>
                    setRegistrationInviteMemberType(
                      event.target.value as RegistrationMemberType,
                    )
                  }
                >
                  {configuredMemberTypeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>}

              <div className="form-group">
                <label className="form-label">Send by</label>
                <select
                  className="form-input"
                  value={registrationInviteChannel}
                  onChange={(event) =>
                    setRegistrationInviteChannel(
                      event.target.value as "Email" | "SMS",
                    )
                  }
                >
                  <option value="Email">Email</option>
                  <option value="SMS">SMS</option>
                </select>
              </div>
            </div>

            {registrationInviteMode === "existing" && (
              <div className="form-group">
                <label className="form-label">Find an existing parent or party</label>
                <PartySearch
                  searchLabel="Search existing parent or party by name"
                  onPartySelected={selectRegistrationInviteParty}
                />
                {registrationInviteParty && (
                  <div className="registration-invite-selected-party">
                    <strong>{registrationInviteParty.displayName}</strong>
                    <span>{registrationInviteParty.partyType === "Family" ? "Family / household" : registrationInviteParty.partyType}</span>
                    {registrationInvitePartySummary?.primaryAddress && (
                      <span>{registrationInvitePartySummary.primaryAddress}</span>
                    )}
                    {registrationInvitePartyLoading && <span>Loading contact details...</span>}
                    {registrationInvitePartySummary && (
                      <div className="registration-invite-contact-details">
                        <div><strong>Email</strong><span>{registrationInviteEmailContacts.map((contact) => contact.value).join(", ") || "Not recorded"}</span></div>
                        <div><strong>Mobile</strong><span>{registrationInviteSmsContacts.map((contact) => contact.value).join(", ") || "Not recorded"}</span></div>
                      </div>
                    )}
                  </div>
                )}
                <p className="field-hint">
                  The secure link opens this party's existing details for review and amendment.
                </p>
              </div>
            )}

            <div className="form-group">
              <label
                className="form-label"
                htmlFor="registration-invite-recipient"
              >
                {registrationInviteChannel === "Email"
                  ? "Email address"
                  : "Mobile number"}
              </label>
              {registrationInviteMode === "existing" ? (
                <>
                  <select
                    id="registration-invite-recipient"
                    className="form-input"
                    value={effectiveRegistrationInviteRecipient}
                    disabled={!registrationInvitePartySummary || registrationInviteChannelContacts.length === 0}
                    onChange={(event) => setRegistrationInviteRecipient(event.target.value)}
                  >
                    {registrationInviteChannelContacts.length === 0 && (
                      <option value="">No recorded {registrationInviteChannel === "Email" ? "email address" : "mobile number"}</option>
                    )}
                    {registrationInviteChannelContacts.map((contact) => (
                      <option key={`${contact.contactType}:${contact.value}`} value={contact.value}>
                        {contact.value}
                      </option>
                    ))}
                  </select>
                  {registrationInvitePartySummary && registrationInviteChannelContacts.length === 0 && (
                    <p className="field-hint">Add a {registrationInviteChannel === "Email" ? "email address" : "mobile number"} to this party before sending.</p>
                  )}
                </>
              ) : (
                <input
                  id="registration-invite-recipient"
                  className="form-input"
                  value={registrationInviteRecipient}
                  onChange={(event) => setRegistrationInviteRecipient(event.target.value)}
                  placeholder={registrationInviteChannel === "Email" ? "name@example.com" : "+447700900000"}
                />
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="registration-invite-instructions">
                Message <span className="field-hint">(optional)</span>
              </label>
              <textarea
                id="registration-invite-instructions"
                className="form-input"
                rows={4}
                maxLength={1000}
                value={registrationInviteInstructions}
                onChange={(event) =>
                  setRegistrationInviteInstructions(event.target.value)
                }
                placeholder="For example: Please complete this form before the first session and have medical and emergency contact details ready."
              />
              <div className="field-hint" style={{ marginBottom: 0 }}>
                Included in the {registrationInviteChannel === "Email" ? "email" : "SMS"} before the {registrationInviteMode === "existing" ? "secure update" : "registration"} link. {registrationInviteInstructions.length}/1000
              </div>
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="registration-invite-link">
                {registrationInviteMode === "existing" ? "Secure update link" : "Registration link"}
              </label>
              <input
                id="registration-invite-link"
                className="form-input"
                value={visibleRegistrationInviteUrl}
                placeholder={registrationInviteMode === "existing" ? "Select a party to generate a secure link" : undefined}
                readOnly
                onFocus={(event) => event.currentTarget.select()}
              />
            </div>

            <div className="registration-qr-panel">
              <div className="registration-qr-preview">
                {registrationQrCode ? (
                  <img
                    src={registrationQrCode}
                    alt={registrationInviteMode === "existing" ? "Secure profile update QR code" : `${registrationMemberTypeLabel(registrationInviteMemberType)} registration QR code`}
                  />
                ) : registrationQrCodeError ? (
                  <span>QR code unavailable</span>
                ) : registrationInviteMode === "existing" && !existingRegistrationLink ? (
                  <span>Select a party to generate a QR code</span>
                ) : (
                  <span>Generating QR code...</span>
                )}
              </div>
              <div className="registration-qr-copy">
                <strong>{registrationInviteMode === "existing" ? "Secure update QR code" : "Registration QR code"}</strong>
                <span>
                  {registrationInviteMode === "existing"
                    ? "Only share this code with the selected party. It opens their existing details."
                    : "People can scan this code to open the same registration link."}
                </span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  disabled={!registrationQrCode}
                  onClick={saveRegistrationQrCode}
                >
                  Save PNG
                </Button>
              </div>
            </div>

            <div className="field-hint" style={{ marginBottom: 0 }}>
              {registrationInviteMode === "existing" ? (
                <>This secure link expires after 14 days and opens the selected party's existing details. It is not a public registration link.</>
              ) : <>This opens{" "}
              {registrationMemberTypeLabel(registrationInviteMemberType)}{" "}
              registration for {serviceName} using the{" "}
              {registrationInvitePartyType === "Family"
                ? "family / household"
                : "single person"}{" "}
              form.</>}
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRegistrationInviteOpen(false)}>
            Close
          </Button>
          <Button
            startIcon={<ContentCopyIcon />}
            disabled={!visibleRegistrationInviteUrl || sendingRegistrationInvite}
            onClick={copyRegistrationInviteLink}
          >
            Copy link
          </Button>
          <Button
            variant="contained"
            startIcon={
              registrationInviteChannel === "Email" ? (
                <EmailOutlinedIcon />
              ) : (
                <SmsOutlinedIcon />
              )
            }
            disabled={
              !canSendCommunications ||
              sendingRegistrationInvite ||
              !effectiveRegistrationInviteRecipient.trim() ||
              (registrationInviteMode === "existing" &&
                (!registrationInviteParty || !registrationInvitePartySummary || !existingRegistrationLink)) ||
              !numericServiceId
            }
            onClick={sendRegistrationInvite}
          >
            Send
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={requirementsOpen}
        onClose={() => setRequirementsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Registration requirements</DialogTitle>
        <DialogContent>
          <div style={{ display: "grid", gap: "1rem", paddingTop: "0.5rem" }}>
            <div className="form-group">
              <label className="form-label">Member type</label>
              <select
                className="form-input"
                value={requirementsMemberType}
                onChange={(event) =>
                  setRequirementsMemberType(
                    event.target.value as RegistrationMemberType,
                  )
                }
              >
                {configuredMemberTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="field-hint" style={{ marginBottom: 0 }}>
              Choose which sections are hidden, optional or required when
              registering this member type for {serviceName}.
            </div>

            {requirementsMemberType === "ServiceUser" &&
              getRegistrationPartyType(requirementsMemberType) === "Family" && (
              <div className="service-members-included-registration-note">
                <strong>Includes primary contact</strong>
                <span>
                  Primary contact details are collected within the service
                  user registration journey.
                </span>
              </div>
            )}

            {loadingRequirements ? (
              <div className="field-hint">Loading requirements...</div>
            ) : (
              <div className="registration-requirements-board">
                <div className="registration-requirement-level-help">
                  {requirementLevelOptions.map((level) => (
                    <div key={level.value}>
                      <strong>{level.label}</strong>
                      <span>{level.hint}</span>
                    </div>
                  ))}
                </div>

                {registrationRequirementGroups.map((group) => (
                  <section
                    key={group.title}
                    className="registration-requirement-group"
                  >
                    <div className="registration-requirement-group-header">
                      <div className="registration-requirement-group-title">
                        {group.title}
                      </div>
                      <div className="field-hint" style={{ marginBottom: 0 }}>
                        {group.description}
                      </div>
                    </div>

                    <div className="registration-requirement-card-list">
                      {group.requirements.map((requirement) => {
                        const componentText = registrationComponentText(
                          requirement.componentKey,
                        );

                        return (
                          <article
                            key={requirement.componentKey}
                            className={`registration-requirement-card registration-requirement-card--${requirement.requirementLevel.toLowerCase()}`}
                          >
                            <div className="registration-requirement-card-copy">
                              <div className="service-registration-requirement-name">
                                {componentText.label}
                              </div>
                              {componentText.description && (
                                <div
                                  className="field-hint"
                                  style={{ marginBottom: 0 }}
                                >
                                  {componentText.description}
                                </div>
                              )}
                            </div>

                            <div
                              className="registration-requirement-segmented"
                              role="radiogroup"
                              aria-label={`${componentText.label} requirement level`}
                            >
                              {requirementLevelOptions.map((level) => (
                                <button
                                  key={level.value}
                                  type="button"
                                  role="radio"
                                  aria-checked={
                                    requirement.requirementLevel === level.value
                                  }
                                  className={`registration-requirement-segment ${
                                    requirement.requirementLevel === level.value
                                      ? "active"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    updateRequirementLevel(
                                      requirement.componentKey,
                                      level.value,
                                    )
                                  }
                                >
                                  <span className="registration-requirement-segment-label">
                                    {level.label}
                                  </span>
                                  <span className="registration-requirement-segment-short-label">
                                    {requirementLevelShortLabels[level.value]}
                                  </span>
                                </button>
                              ))}
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequirementsOpen(false)}>Close</Button>
          <Button
            variant="contained"
            disabled={loadingRequirements || savingRequirements}
            onClick={saveRegistrationRequirements}
          >
            Save requirements
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
